import { useMutation, useQueries, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { useToast } from '@/components/feedback/Toaster';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import * as intakeApi from '../intake.api';
import { useZonderGekozenKlant } from './useZonderGekozenKlant';
import type { CreateIntakeSessionInput, IntakeList, IntakeListStatus, IntakeSession, IntakeSessionFlow, UpdateIntakeColumnInput } from '../intake.api';

export const INTAKE_SESSIONS_QUERY_KEY = ['intake', 'sessions'] as const;

/** h5-3: while interpretation is in flight, poll every 2s so the UI reflects progress live. */
const INTAKE_POLL_INTERVAL_MS = 2000;

function isInterpretationInFlight(status: IntakeListStatus): boolean {
  return status === 'uploaded' || status === 'interpreting';
}

/** spec-aanmeldketen-sneller CAP-3: het afronden loopt op de achtergrond; blijven ophalen tot de uitkomst. */
function isSubmitInFlight(status: IntakeListStatus): boolean {
  return status === 'submitting';
}

/**
 * spec-cp-aanmeldingen-in-lijst: de ene Aanmeldingen-lijst. De sleutel valt onder
 * `INTAKE_SESSIONS_QUERY_KEY`, zodat elke bestaande invalidatie na uploaden of indienen ook deze lijst
 * ververst.
 */
export function useIntakeList() {
  const { isSimulating, simulatedGln } = useEffectiveAccess();
  const zonderKlant = useZonderGekozenKlant();
  // Per klant een eigen lijst: anders ziet een medewerker die van klant wisselt even de vorige lijst.
  const gln = isSimulating ? simulatedGln : null;

  return useQuery({
    queryKey: [...INTAKE_SESSIONS_QUERY_KEY, 'list', gln],
    queryFn: intakeApi.getIntakeList,
    enabled: !zonderKlant,
    refetchInterval: (query: Query<IntakeList>) => {
      const rijen = query.state.data?.data ?? [];
      return rijen.some((rij) => isInterpretationInFlight(rij.status) || isSubmitInFlight(rij.status))
        ? INTAKE_POLL_INTERVAL_MS
        : false;
    },
  });
}

export function useIntakeSession(sessionId: string | null) {
  // Zonder gekozen klant geen detailaanvraag: die zou de achterkant met 400 weigeren.
  const zonderKlant = useZonderGekozenKlant();
  return useQuery({
    queryKey: ['intake', 'sessions', sessionId],
    queryFn: () => intakeApi.getIntakeSession(sessionId!),
    enabled: !!sessionId && !zonderKlant,
    refetchInterval: (query: Query<IntakeSession>) => {
      const session = query.state.data;
      if (!session) return false;
      if (isSubmitInFlight(session.status)) return INTAKE_POLL_INTERVAL_MS;
      return isInterpretationInFlight(session.status) && !session.interrupted ? INTAKE_POLL_INTERVAL_MS : false;
    },
  });
}

export function useCreateIntakeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateIntakeSessionInput) => intakeApi.createIntakeSession(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INTAKE_SESSIONS_QUERY_KEY });
    },
  });
}

export function useRetryIntakeInterpretation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => intakeApi.retryIntakeInterpretation(sessionId),
    onSuccess: (session) => {
      queryClient.setQueryData(['intake', 'sessions', session.id], session);
      void queryClient.invalidateQueries({ queryKey: INTAKE_SESSIONS_QUERY_KEY });
    },
  });
}

/** h5-5: static reference data — never changes within a session, so no polling/refetch needed. */
export function useGs1FieldOptions(flow: IntakeSessionFlow = 'product') {
  return useQuery({
    // h7-1: de flow hoort in de sleutel — anders krijgt een verpakkingssessie de productlijst die
    // een eerder geopende productsessie in de cache achterliet.
    queryKey: ['intake', 'gs1-fields', flow],
    queryFn: () => intakeApi.getGs1FieldOptions(flow),
    staleTime: Infinity,
  });
}

export function useUpdateIntakeColumn(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateIntakeColumnInput) => intakeApi.updateIntakeColumn(sessionId, input),
    onSuccess: (session) => {
      queryClient.setQueryData(['intake', 'sessions', sessionId], session);
    },
  });
}

export function useValidateIntakeSession(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['intake', 'sessions', sessionId, 'validation'],
    queryFn: () => intakeApi.validateIntakeSession(sessionId),
    enabled,
  });
}

export function useSubmitIntakeSession(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expectedVersion: number) => intakeApi.submitIntakeSession(sessionId, expectedVersion),
    onSuccess: (session) => {
      queryClient.setQueryData(['intake', 'sessions', sessionId], session);
      void queryClient.invalidateQueries({ queryKey: INTAKE_SESSIONS_QUERY_KEY });
    },
    onError: () => {
      // Ook — en juist — na een fout opnieuw ophalen. Het indienen hoogt het versienummer van de
      // sessie op, óók als de keten daarna struikelt. Blijft het scherm op het oude nummer staan,
      // dan botst de volgende klik op indienen met een 409 die de klant niets zegt, en blijft hij
      // dat lezen tot hij de pagina ververst. De melding over de vorige poging komt met dezelfde
      // ophaalactie mee.
      void queryClient.invalidateQueries({ queryKey: ['intake', 'sessions', sessionId] });
    },
  });
}

/** h5-8 FR38a: change detection (DQS + local check) for the review screen. Only enabled once the
 * session has reached review, since detection needs a gtin mapping to exist. */
export function useIntakeSessionChanges(sessionId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['intake', 'sessions', sessionId, 'changes'],
    queryFn: () => intakeApi.getIntakeSessionChanges(sessionId),
    enabled,
  });
}

/** h5-8 FR38a: the customer's per-product exclude/proceed choice. */
export function useUpdateProductDecision(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: intakeApi.UpdateProductDecisionInput) => intakeApi.updateProductDecision(sessionId, input),
    onSuccess: (session) => {
      queryClient.setQueryData(['intake', 'sessions', sessionId], session);
    },
  });
}

/** h5-7 FR35: Beheer version-history view for a single GLN's mapping profile. */
export function useMappingProfileVersions(gln: string | null) {
  return useQuery({
    queryKey: ['mapping-profiles', gln],
    queryFn: () => intakeApi.getMappingProfileVersions(gln!),
    enabled: !!gln,
  });
}

/** h5-7 FR35: Beheer reset — always paired with a mandatory audit note. */
export function useResetMappingProfile(gln: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (note: string) => intakeApi.resetMappingProfile(gln!, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mapping-profiles', gln] });
    },
  });
}

/**
 * h13-4/h13-5: de afnemers van één doelmarkt, met hoe vaak deze klant ze eerder koos.
 *
 * Binnen één aanmelding verandert een geslaagd antwoord niet, dus dát wordt niet opnieuw opgehaald.
 *
 * H13-9: een bron die zweeg is géén antwoord om te bewaren. Bleef `available: false` hier een
 * gewone uitkomst, dan hield `staleTime: Infinity` die lege lijst het hele bezoek vast: de klant
 * kon geen afnemer meer kiezen, mocht dus niet indienen, en niets vertelde hem dat herladen hielp.
 * Als mislukte poging doet react-query hem over; blijft de bron weg, dan maakt `combine` er alsnog
 * de bestaande informatieregel van.
 */
export function useRetailerHistory(targetMarkets: string[]) {
  // De historie hoort bij één klant: per klant een eigen cache, anders ziet een medewerker die van
  // klant wisselt de afnemers van de vorige klant (de cache verloopt hier nooit).
  const { isSimulating, simulatedGln } = useEffectiveAccess();
  const gln = isSimulating ? simulatedGln : null;
  return useQueries({
    queries: targetMarkets.map((targetMarket) => ({
      queryKey: ['intake', 'retailer-history', gln, targetMarket],
      queryFn: async () => {
        const historie = await intakeApi.getRetailerHistory(targetMarket);
        // Een lége lijst mét `available` is wél een antwoord: deze klant kent hier nog geen
        // afnemers. Die hoort een gewone open lijst te krijgen, geen herhaalpoging.
        if (!historie.available) throw new Error(`retailer history unavailable for ${targetMarket}`);
        return historie;
      },
      staleTime: Infinity,
      // Expliciet, en niet geleend van de standaard in `query-client.ts`: zonder deze regel
      // verdwijnt de herhaalpoging zodra iemand die standaard ooit op `false` zet, en blijft élke
      // toets hier groen terwijl de klant weer met een lege lijst zit.
      retry: 1,
    })),
    // Een mislukt verzoek is voor het scherm hetzelfde als een bron die zwijgt: geen voorstel. Zou
    // hier alleen `data` doorgegeven worden, dan bleef er een lege lijst over zónder de regel die
    // zegt waarom — en dan lijkt het alsof deze markt helemaal geen afnemers kent.
    combine: (results) =>
      results.map((result) => ({
        data: result.data ?? (result.isError ? { retailers: [], available: false } : undefined),
        isLoading: result.isLoading,
      })),
  });
}

/** h13-5: de gekozen afnemers van één doelmarkt, bij élke vinkje-klik weggeschreven. */
export function useUpdateRetailerChoice(sessionId: string) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (input: intakeApi.UpdateRetailerChoiceInput) => intakeApi.updateRetailerChoice(sessionId, input),
    onSuccess: (session) => {
      queryClient.setQueryData(['intake', 'sessions', sessionId], session);
    },
    onError: () => {
      // Zeg het. Het vinkje springt na het opnieuw ophalen gewoon terug, en zonder melding denkt de
      // klant dat zijn keuze bewaard is terwijl er niets staat.
      addToast({ key: 'intake.review.retailers.saveFailed' }, 'error');
      // Een botsing met een tweede tabblad hoogt het versienummer op. Zonder deze ophaalactie blijft
      // het scherm op het oude nummer staan en botst élke volgende klik opnieuw.
      void queryClient.invalidateQueries({ queryKey: ['intake', 'sessions', sessionId] });
    },
  });
}
